import React, { useCallback, useMemo } from "react";
import "../Campaign.css";
import CampaignActionBar from "./CampaignActionBar";
import CampaignFilterBar from "./CampaignFilterBar";
import CampaignPagination from "./CampaignPagination";
import CampaignSummary from "./CampaignSummary";
import CampaignTable from "./CampaignTable";

/**
 * 캠페인 페이지 레이아웃 컴포넌트
 * @param {Object} props
 * @param {Object} props.config - 캠페인 설정 객체
 * @param {Object} props.data - { items, summary } 데이터
 * @param {Object} props.filter - 필터 상태
 * @param {Array} props.selectedIds - 선택된 ID 배열
 * @param {boolean} props.loading - 로딩 상태
 * @param {Object} props.handlers - 핸들러 객체
 */
function CampaignPageLayout({
  config,
  data = { items: [], summary: {} },
  filter = {},
  selectedIds = [],
  loading = false,
  handlers = {},
}) {
  const {
    onFilterChange,
    onFilterApply,
    onFilterReset,
    onSelect,
    onSelectAll,
    onPageChange,
    onAction,
    onDownload,
    onRefundOpen,
  } = handlers;

  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload(selectedIds, data.items);
    }
  }, [onDownload, selectedIds, data.items]);

  const modals = useMemo(() => {
    if (!config?.modals) return null;
    if (!config.modals) return null;
    return config.modals.map((modalConfig) => {
      const ModalComponent = modalConfig.component;
      if (!ModalComponent) return null;

      const isOpen = modalConfig.openWhen
        ? modalConfig.openWhen(selectedIds, data.items, filter)
        : false;

      const modalProps = modalConfig.propsBuilder
        ? modalConfig.propsBuilder(selectedIds, data.items, filter)
        : {};

      return (
        <ModalComponent
          key={modalConfig.id}
          open={isOpen}
          onClose={() => {
            // 모달 닫기 로직 (필요시 handlers에 추가)
          }}
          {...modalProps}
        />
      );
    });
  }, [config?.modals, selectedIds, data.items, filter]);

  if (!config) {
    return <div>설정이 제공되지 않았습니다.</div>;
  }

  return (
    <div className="campaign-management-container" data-campaign-id={config.id}>
      {/* Summary */}
      {config.summary && (
        <CampaignSummary
          summary={data.summary}
          fields={config.summary.fields}
        />
      )}

      {/* Filter Bar */}
      {config.filters && (
        <CampaignFilterBar
          schema={config.filters.schema}
          filter={handlers.tempFilter || filter}
          defaults={config.filters.default}
          onChange={onFilterChange}
          onApply={onFilterApply}
          onReset={onFilterReset}
        />
      )}

      {/* Table Header & Action Bar */}
      <div className="table-header">
        <h3 className="section-title">{config.title || "캠페인 관리"}</h3>
        {/* Action Bar */}
        <div className="action-buttons">
          {config.download && onDownload && (
            <button
              className="export-btn"
              onClick={handleDownload}
              disabled={loading}
            >
              시트로 내보내기
            </button>
          )}
          {config.actions && (
            <CampaignActionBar
              actions={config.actions}
              selectedIds={selectedIds}
              items={data.items}
              onAction={onAction}
              onRefundOpen={onRefundOpen}
              isLoading={loading}
            />
          )}
        </div>
      </div>

      {/* Table */}
      {config.table && (
        <CampaignTable
          columns={config.table.columns}
          data={data.items}
          keyField={config.table.keyField}
          selectedIds={selectedIds}
          onSelect={onSelect}
          onSelectAll={onSelectAll}
          rowClick={config.table.rowClick}
          handlers={handlers}
        />
      )}

      {/* Pagination */}
      {config.filters && (
        <CampaignPagination
          page={filter.page || 1}
          limit={filter.limit || 50}
          totalCount={data.summary.totalCount || 0}
          onPageChange={onPageChange}
        />
      )}

      {/* Modals */}
      {modals}
    </div>
  );
}

export default React.memo(CampaignPageLayout);
